import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * MiniAreaChart - compact area chart for strategy cards
 * @param {{date: string, value: number}[]} data
 */
const MiniAreaChart = ({ data = [] }) => {
  const currentTheme = useTheme();
  const values = Array.isArray(data) ? data.map((d) => Number(d.value)) : [];
  const hasData = values.length > 0 && values.every((v) => !Number.isNaN(v));
  const min = hasData ? Math.min(...values) : 0;
  const max = hasData ? Math.max(...values) : 1;
  const pad = Math.max((max - min) * 0.1, 0.02);
  const domain = [min - pad, max + pad];

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
        <defs>
          <linearGradient id="miniColorValue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={currentTheme.palette.primary.main} stopOpacity={0.7} />
            <stop offset="95%" stopColor={currentTheme.palette.primary.main} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.palette.divider} vertical={false} />
        <XAxis dataKey="date" hide />
        <YAxis domain={domain} hide />
        <Tooltip
          formatter={(value) => (typeof value === "number" ? value.toFixed(3) : value)}
          labelFormatter={(label) => `Date: ${label}`}
          contentStyle={{
            backgroundColor: currentTheme.palette.background.default,
            border: `1px solid ${currentTheme.palette.divider}`,
            borderRadius: 8,
            color: currentTheme.palette.text.primary,
          }}
        />
        <Area type="monotone" dataKey="value" stroke={currentTheme.palette.primary.main} fillOpacity={1} fill="url(#miniColorValue)" isAnimationActive={false}/>
      </AreaChart>
    </ResponsiveContainer>
  );
};

/**
 * StrategyCard Component
 * Displays an investment strategy card with image or initials placeholder
 * 
 * @param {string} strategyID - Unique identifier for the strategy
 * @param {string} title - Strategy title/name
 * @param {string} description - Strategy description
 * @param {string} image - Optional image URL (if not provided, shows initials)
 * @param {function} onAddToCart - Callback function when "Add to Cart" is clicked
 * @param {{date: string, value: number}[]} chartData - Optional data to render mini chart
 * @param {string} strategistID - Optional strategist ID to show in the card
 */
const StrategyCard = ({ strategyID, title, description, image, onAddToCart, chartData = [], loading = false, sharpeRatio, strategistID = "strategist_1" }) => {
  
  const theme = useTheme();

  // Generate initials from title
  const getInitials = (text) => {
    if (!text) return "ST";
    const words = text.trim().split(" ");
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const initials = getInitials(title);
  const [imgError, setImgError] = useState(false);

  const navigate = useNavigate()
  const location = useLocation()

  const handleCardClick = () => {
    navigate(`/store/strategy/${strategyID}`, { state:
      { from: location.pathname + location.search }
    });
  };

  const handleStrategistClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/strategist/${strategistID}`);
  };

  return (
    <Card sx={{
      width: "100%",
      height: '100%',
      position: "relative",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      border: `1px solid ${theme.palette.divider}`,
      transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
      cursor: "pointer",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      }
    }}
    onClick={handleCardClick}
    >
      {Array.isArray(chartData) && chartData.length > 0 ? (
        <Box sx={{
          height: 180,
          backgroundColor: 'background.default',
          filter: loading ? 'blur(4px)' : 'none',
          transition: 'filter 0.3s ease-in-out'
        }}>
          <MiniAreaChart data={chartData} />
        </Box>
      ) : image && !imgError ? (
        <CardMedia
          component="img"
          sx={{ height: 180, objectFit: "cover" }}
          image={image}
          alt={title}
          onError={() => setImgError(true)}
        />
      ) : (
        <Box
          sx={{
            height: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "action.hover",
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              fontSize: "2rem",
              fontWeight: 600,
              backgroundColor: "action.disabled",
              color: "background.paper",
            }}
          >
            {initials}
          </Avatar>
        </Box>
      )}
      <CardContent>
        <Typography gutterBottom variant="h5" component="div">
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', marginTop: '8px' }}>
          Sharpe Ratio: {sharpeRatio?.toFixed(4) || 'N/A'}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default StrategyCard;
